import { createRouter, createWebHistory } from 'vue-router'
import { authGuardMiddleware } from '../middlewares/authGuard'
import { accountOnboardingGuardMiddleware } from '../middlewares/accountOnboardingGuard'
import useFeatureFlag from '../composables/useFeatureFlag'

import {
  DASHBOARD_ROUTE_PATH,
  DEVICES_OVERVIEW_ROUTE_PATH,
  DEVICES_SINGLE_ROUTE_PATH,
  DEVICES_SINGLE_CONTAINERS_ROUTE_PATH,
  DEVICES_SINGLE_LOGS_ROUTE_PATH,
  DEVICES_SINGLE_TERMINAL_ROUTE_PATH,
  ACCESS_CONTROLS_ROUTE_PATH,
  ACCESS_CONTROLS_GROUPS_ROUTE_PATH,
  ACCESS_CONTROLS_POLICIES_ROUTE_PATH,
  ACCESS_CONTROLS_POLICIES_SINGLE_ROUTE_PATH,
  LOGIN_ROUTE_PATH,
  ONBOARDING_PATH,
  SETTINGS_PATH,
  SETTINGS_PLAN_PATH,
  SETTINGS_CHANGE_PLAN_PATH,
  SETTINGS_CHANGE_PLAN_SUCCESS_PATH,
  SIGNUP_ROUTE_PATH,
  SETTINGS_SECURITY_PATH,
  SETTINGS_BILLING_PATH
} from '../constants/Routes'
import { FEATURE_FLAGS } from '@/constants/FeatureFlags'
import logger from '@/logger'

/**
 * Feature Flag Guard Middleware
 *
 * This middleware checks if a feature flag is enabled for the current account using the useFeatureFlag composable.
 * If the feature flag is not enabled (or fails to load), the user is redirected to the dashboard.
 * The composable handles fetching the data if necessary.
 *
 * @param {string} flagName - The name of the feature flag to check.
 * @returns {Function} - The middleware function.
 *
 * @example
 * // Example usage in a route definition:
 * // {
 * //   path: '/feature-gated-route',
 * //   name: 'FeatureGatedRoute',
 * //   component: () => import('../views/FeatureGatedView.vue'),
 * //   beforeEnter: [authGuardMiddleware, featureFlagGuardMiddleware('new-feature')]
 * // }
 */
export const featureFlagGuardMiddleware = (flagName) => {
  return async (to, from, next) => {
    const { isEnabled } = useFeatureFlag()

    try {
      const enabled = await isEnabled(flagName) // isEnabled now handles loading/checking

      if (enabled) {
        logger.debug({ msg: `Access granted to route ${to.path} for flag '${flagName}'.` })
        next() // Proceed if enabled
      } else {
        logger.debug({
          msg: `Access restricted to route ${to.path}, flag '${flagName}' not enabled or check failed. Redirecting.`
        })
        next({ path: DASHBOARD_ROUTE_PATH }) // Redirect if not enabled or error occurred during check
      }
    } catch (error) {
      // Catch unexpected errors from the composable itself, though it should return boolean
      logger.error({
        msg: `Unexpected error in featureFlagGuardMiddleware for ${to.path} checking flag '${flagName}':`,
        error
      })
      next({ path: DASHBOARD_ROUTE_PATH })
    }
  }
}

/**
 * Route Definitions
 */
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: DASHBOARD_ROUTE_PATH,
      redirect: DEVICES_OVERVIEW_ROUTE_PATH
    },
    {
      path: DEVICES_OVERVIEW_ROUTE_PATH,
      beforeEnter: [authGuardMiddleware, accountOnboardingGuardMiddleware],
      children: [
        {
          path: '',
          component: () => import('../views/Devices/DevicesOverviewView.vue')
        },
        {
          path: DEVICES_SINGLE_ROUTE_PATH,
          component: () => import('../views/Devices/DeviceView.vue'),
          children: [
            {
              path: '',
              name: 'device-single',
              component: () => import('../views/Devices/DeviceGeneralView.vue')
            },
            {
              path: DEVICES_SINGLE_CONTAINERS_ROUTE_PATH,
              component: () => import('../views/Devices/DeviceContainersView.vue')
            },
            {
              path: DEVICES_SINGLE_LOGS_ROUTE_PATH,
              component: () => import('../views/Devices/DeviceLogsView.vue')
            },
            {
              path: DEVICES_SINGLE_TERMINAL_ROUTE_PATH,
              component: () => import('../views/Devices/DeviceTerminalView.vue')
            }
          ]
        }
      ]
    },
    {
      path: ACCESS_CONTROLS_ROUTE_PATH,
      beforeEnter: [authGuardMiddleware, accountOnboardingGuardMiddleware],
      redirect: () => {
        return { path: ACCESS_CONTROLS_GROUPS_ROUTE_PATH }
      },
      children: [
        {
          path: ACCESS_CONTROLS_GROUPS_ROUTE_PATH,
          component: () => import('../views/AccessControls/AccessGroupsView.vue'),
          beforeEnter: [featureFlagGuardMiddleware(FEATURE_FLAGS.ACCESS_GROUPS)]
        },
        {
          path: ACCESS_CONTROLS_POLICIES_ROUTE_PATH,
          component: () => import('../views/AccessControls/AccessPolicies/AccessPoliciesView.vue'),
          beforeEnter: [featureFlagGuardMiddleware(FEATURE_FLAGS.ACCESS_POLICIES)]
        },
        {
          path: ACCESS_CONTROLS_POLICIES_SINGLE_ROUTE_PATH,
          component: () => import('../views/AccessControls/AccessPolicies/AccessPolicyView.vue'),
          beforeEnter: [featureFlagGuardMiddleware(FEATURE_FLAGS.ACCESS_POLICIES)]
        }
      ]
    },
    {
      path: SETTINGS_PATH,
      component: () => import('../views/Settings/SettingsView.vue'),
      beforeEnter: [authGuardMiddleware, accountOnboardingGuardMiddleware],
      children: [
        {
          path: SETTINGS_PATH,
          component: () => import('../views/Settings/ProfileView.vue')
        },
        {
          path: SETTINGS_PLAN_PATH,
          component: () => import('../views/Settings/Plan/PlanView.vue')
        },
        {
          path: SETTINGS_CHANGE_PLAN_PATH,
          component: () => import('../views/Settings/Plan/ChangePlanView.vue'),
          meta: {
            hideSidenav: true
          }
        },
        {
          path: SETTINGS_CHANGE_PLAN_SUCCESS_PATH,
          component: () => import('../views/Settings/Plan/ChangePlanSuccessView.vue'),
          meta: {
            hideSidenav: true
          }
        },
        {
          path: SETTINGS_SECURITY_PATH,
          component: () => import('../views/Settings/SecurityView.vue')
        },
        {
          path: SETTINGS_BILLING_PATH,
          component: () => import('../views/Settings/BillingView.vue')
        }
      ]
    },
    {
      name: 'onboarding',
      path: ONBOARDING_PATH,
      component: () => import('../views/OnboardingView.vue'),
      beforeEnter: [authGuardMiddleware],
      meta: {
        minimalNavbar: true
      }
    },
    {
      name: 'signup',
      path: SIGNUP_ROUTE_PATH,
      component: () => import('../views/SignUpView.vue'),
      meta: {
        hideNavbar: true,
        requiresAuth: false
      }
    },
    {
      name: 'login',
      path: LOGIN_ROUTE_PATH,
      component: () => import('../views/SignInView.vue'),
      meta: {
        hideNavbar: true,
        requiresAuth: false
      }
    }
  ]
})

export default router
